import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import {
  B2bAdminModel,
  CreateB2bAdminRequest,
  CreatePartnerEmployeeRequest,
  PartnerEmployeeModel,
  SearchPartnerEmployeesRequest,
  UpdateB2bAdminRequest,
  UpdatePartnerEmployeeRequest,
} from '@app/core/models/partner/partner-employee.model';
import { PaginatedResponse } from '@app/core/models/shared/admin.model';

const EMPTY_EMPLOYEES_PAGE: PaginatedResponse<PartnerEmployeeModel> = {
  currentPageNumber: 0,
  pageSize: 0,
  totalElements: 0,
  totalPages: 0,
  isLastPage: true,
  isFirstPage: true,
  isEmpty: true,
  content: [],
};

@Injectable({
  providedIn: 'root',
})
export class PartnerEmployeeService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  // Swagger uses "/b2b/partner-employees" for GET endpoints
  private get listBaseUrl(): string {
    return `${this.apiBaseUrl}/b2b/partner-employees`;
  }

  // POST endpoint shown in Swagger uses underscore variant
  private get createBaseUrl(): string {
    return `${this.apiBaseUrl}/b2b/partner-employees`;
  }

  create(body: CreatePartnerEmployeeRequest): Observable<PartnerEmployeeModel> {
    this.isLoading.set(true);
    return this.http.post<PartnerEmployeeModel>(this.createBaseUrl, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании роли:', error);
        // 409 (EMAIL_EXIST и т.п.) показывает диалог — под конкретным полем
        if (!(error instanceof HttpErrorResponse && error.status === 409)) {
          this.translate.get('Ошибка при создании роли').subscribe(msg => {
            this.snackbarService.error(msg);
          });
        }
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  createB2bAdmin(body: CreateB2bAdminRequest): Observable<B2bAdminModel> {
    this.isLoading.set(true);
    return this.http.post<B2bAdminModel>(`${this.apiBaseUrl}/b2b/admins`, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании B2B администратора:', error);
        this.translate.get('Ошибка при создании роли').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  getB2bAdminsPage(page: number, size: number): Observable<PaginatedResponse<B2bAdminModel>> {
    this.isLoading.set(true);
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http
      .get<PaginatedResponse<B2bAdminModel>>(`${this.apiBaseUrl}/b2b/admins`, { params })
      .pipe(
        catchError(error => {
          console.error('Ошибка при получении B2B администраторов:', error);
          this.translate.get('Ошибка при получении списка ролей').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of({
            ...EMPTY_EMPLOYEES_PAGE,
            content: [],
          } as PaginatedResponse<B2bAdminModel>);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  updateB2bAdmin(userId: string, body: UpdateB2bAdminRequest): Observable<B2bAdminModel> {
    if (!userId) throw new Error('userId is required');
    this.isLoading.set(true);
    return this.http.put<B2bAdminModel>(`${this.apiBaseUrl}/b2b/admins/${userId}`, body).pipe(
      catchError(error => {
        console.error('Ошибка при обновлении B2B администратора:', error);
        this.translate.get('Ошибка при получении списка ролей').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  deleteB2bAdmin(userId: string): Observable<void> {
    if (!userId) throw new Error('userId is required');
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.apiBaseUrl}/b2b/admins/${userId}`).pipe(
      catchError(error => {
        console.error('Ошибка при удалении B2B администратора:', error);
        this.translate.get('Ошибка при получении списка ролей').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  activateB2bAdmin(userId: string): Observable<B2bAdminModel> {
    if (!userId) throw new Error('userId is required');
    this.isLoading.set(true);
    return this.http.patch<B2bAdminModel>(`${this.apiBaseUrl}/b2b/admins/${userId}/activate`, {}).pipe(
      catchError(error => {
        console.error('Ошибка при активации B2B администратора:', error);
        this.translate.get('Не удалось активировать пользователя').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  deactivateB2bAdmin(userId: string): Observable<B2bAdminModel> {
    if (!userId) throw new Error('userId is required');
    this.isLoading.set(true);
    return this.http
      .patch<B2bAdminModel>(`${this.apiBaseUrl}/b2b/admins/${userId}/deactivate`, {})
      .pipe(
        catchError(error => {
          console.error('Ошибка при деактивации B2B администратора:', error);
          this.translate.get('Не удалось деактивировать пользователя').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          throw error;
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  getByPartnerId(partnerId: string): Observable<PartnerEmployeeModel[]> {
    if (!partnerId) return of([]);
    this.isLoading.set(true);
    return this.http.get<PartnerEmployeeModel[]>(`${this.listBaseUrl}/partner/${partnerId}`).pipe(
      catchError(error => {
        console.error('Ошибка при получении списка ролей партнера:', error);
        this.translate.get('Ошибка при получении списка ролей').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  getByNetworkId(partnerNetworkId: string): Observable<PartnerEmployeeModel[]> {
    if (!partnerNetworkId) return of([]);
    this.isLoading.set(true);
    return this.http
      .get<PartnerEmployeeModel[]>(`${this.listBaseUrl}/network/${partnerNetworkId}`)
      .pipe(
        catchError(error => {
          console.error('Ошибка при получении списка ролей сети:', error);
          this.translate.get('Ошибка при получении списка ролей').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of([]);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  getByStoreId(partnerStoreId: string): Observable<PartnerEmployeeModel[]> {
    if (!partnerStoreId) return of([]);
    this.isLoading.set(true);
    return this.http
      .get<PartnerEmployeeModel[]>(`${this.listBaseUrl}/store/${partnerStoreId}`)
      .pipe(
        catchError(error => {
          console.error('Ошибка при получении списка ролей магазина:', error);
          this.translate.get('Ошибка при получении списка ролей').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of([]);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  searchPartnerEmployeesPage(
    page: number,
    size: number,
    body: SearchPartnerEmployeesRequest
  ): Observable<PaginatedResponse<PartnerEmployeeModel>> {
    this.isLoading.set(true);
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http
      .post<PaginatedResponse<PartnerEmployeeModel>>(`${this.listBaseUrl}/search`, body, { params })
      .pipe(
        catchError(error => {
          console.error('Ошибка при поиске ролей (page):', error);
          this.translate.get('Ошибка при получении списка ролей').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of(EMPTY_EMPLOYEES_PAGE);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  getById(employeeId: string): Observable<PartnerEmployeeModel | null> {
    if (!employeeId) return of(null);
    this.isLoading.set(true);
    return this.http.get<PartnerEmployeeModel>(`${this.listBaseUrl}/${employeeId}`).pipe(
      catchError(error => {
        console.error('Ошибка при получении роли:', error);
        this.translate.get('Ошибка при получении списка ролей').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of(null);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  update(employeeId: string, body: UpdatePartnerEmployeeRequest): Observable<PartnerEmployeeModel> {
    if (!employeeId) throw new Error('employeeId is required');
    this.isLoading.set(true);
    return this.http.put<PartnerEmployeeModel>(`${this.listBaseUrl}/${employeeId}`, body).pipe(
      catchError(error => {
        console.error('Ошибка при обновлении роли:', error);
        // 409 (EMAIL_EXIST и т.п.) показывает диалог — под конкретным полем
        if (!(error instanceof HttpErrorResponse && error.status === 409)) {
          this.translate.get('Ошибка при получении списка ролей').subscribe(msg => {
            this.snackbarService.error(msg);
          });
        }
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  delete(employeeId: string): Observable<void> {
    if (!employeeId) throw new Error('employeeId is required');
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.listBaseUrl}/${employeeId}`).pipe(
      catchError(error => {
        console.error('Ошибка при удалении роли:', error);
        this.translate.get('Ошибка при получении списка ролей').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  activate(employeeId: string): Observable<void> {
    if (!employeeId) throw new Error('employeeId is required');
    this.isLoading.set(true);
    return this.http.patch<void>(`${this.listBaseUrl}/${employeeId}/activate`, {}).pipe(
      catchError(error => {
        console.error('Ошибка при активации пользователя партнера:', error);
        this.translate.get('Не удалось активировать пользователя').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  deactivate(employeeId: string): Observable<void> {
    if (!employeeId) throw new Error('employeeId is required');
    this.isLoading.set(true);
    return this.http.patch<void>(`${this.listBaseUrl}/${employeeId}/deactivate`, {}).pipe(
      catchError(error => {
        console.error('Ошибка при деактивации пользователя партнера:', error);
        this.translate.get('Не удалось деактивировать пользователя').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }
}
